import React, { useCallback, useState, useEffect } from 'react';
import { styled } from '@compiled/react';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import { SuccessAutoDismissFlag, AkFlag } from '@atlassian/jira-flags'; // ignore-for-ENGHEALTH-17759
import { useIntl } from '@atlassian/jira-intl';
import { isWorkTypeAvgLength } from '@atlassian/jira-providers-issue/src/utils.tsx';
import { getIssueDiscoveryUrl } from '../../common/utils.tsx';
import messages from './messages.tsx';

const GLOBAL_CREATE_POLLINATOR_DATA_TEST_ID = 'jira-issue-create.modal.create-form.success-flag';

type Props = {
	cardKeys: string[];
	id: string | number;
	onDismissed: undefined | ((arg1: string | number) => void);
	isAutoDismissed?: boolean;
	issueType?: string;
};

export const CardCreateGlobalFlag = ({
	onDismissed,
	id,
	cardKeys,
	isAutoDismissed = true,
	issueType,
}: Props) => {
	const { formatMessage } = useIntl();
	const [isCopied, setCopied] = useState(false);

	useEffect(() => {
		let timeoutId: ReturnType<typeof setTimeout>;
		if (isCopied) {
			timeoutId = setTimeout(() => {
				setCopied(false);
			}, 2000);
		}
		return () => {
			timeoutId && clearTimeout(timeoutId);
		};
	}, [isCopied]);

	const issueDiscoveryUrl = getIssueDiscoveryUrl(cardKeys);

	const copyToClipboard = useCallback((): void => {
		if (fg('jfp-magma-undefined-navigator')) {
			globalThis.navigator.clipboard.writeText(
				`${globalThis.window.location.origin}${issueDiscoveryUrl}`,
			);
		} else {
			// eslint-disable-next-line jira/jira-ssr/no-unchecked-globals-usage
			navigator.clipboard.writeText(`${window.location.origin}${issueDiscoveryUrl}`);
		}
		setCopied(true);
	}, [issueDiscoveryUrl]);

	const getFlagTitle = () => {
		if (
			cardKeys.length <= 1 &&
			issueType &&
			expVal('jira-itr-work-type-exp-1', 'isEnabled', false)
		) {
			return formatMessage(messages.createSuccessFlagTitleIssueTerminologyRefreshM4, {
				issueKey: cardKeys[0],
				workType: issueType,
			});
		}

		return cardKeys.length > 1
			? formatMessage(
					expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? messages.multiplesIssuesCreatedSuccessFlagTitleIssueTermRefresh
						: messages.multiplesIssuesCreatedSuccessFlagTitle,
					{
						issueCount: cardKeys.length,
					},
				)
			: formatMessage(
					expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
						? messages.createSuccessFlagTitleIssueTermRefresh
						: messages.createSuccessFlagTitle,
					{
						issueKey: cardKeys[0],
					},
				);
	};

	const getViewMessage = () => {
		if (
			issueType &&
			isWorkTypeAvgLength(issueType) &&
			expVal('jira-itr-work-type-exp-1', 'isEnabled', false)
		) {
			return cardKeys.length > 1
				? messages.viewIssueLinkMultipleTextIssueTerminologyRefreshM4
				: messages.viewIssueLinkTextIssueTerminologyRefreshM4;
		}
		if (expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)) {
			return messages.viewIssueLinkTextIssueTermRefresh;
		}
		return messages.viewIssueLinkText;
	};

	const actions = [
		{
			content: formatMessage(getViewMessage(), {
				numIssues: cardKeys.length,
				workType: issueType,
			}),
			href: issueDiscoveryUrl,
		},
		{
			content: isCopied ? (
				<CopySuccess>{formatMessage(messages.linkCopied)}</CopySuccess>
			) : (
				formatMessage(messages.copyLink)
			),
			onClick: () => {
				copyToClipboard();
			},
		},
	];

	if (!isAutoDismissed && fg('show-create-success-flag-once-in-backlog-page')) {
		return (
			<div data-testid={GLOBAL_CREATE_POLLINATOR_DATA_TEST_ID} data-issue-key={cardKeys[0]}>
				<AkFlag id={id} onDismissed={onDismissed} title={getFlagTitle()} actions={actions} />
			</div>
		);
	}
	return (
		<div data-testid={GLOBAL_CREATE_POLLINATOR_DATA_TEST_ID} data-issue-key={cardKeys[0]}>
			<SuccessAutoDismissFlag
				id={id}
				onDismissed={onDismissed}
				title={getFlagTitle()}
				actions={actions}
			/>
		</div>
	);
};

// eslint-disable-next-line @atlaskit/ui-styling-standard/no-styled -- To migrate as part of go/ui-styling-standard
const CopySuccess = styled.span({
	color: token('color.background.success.pressed'),
});
